import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import { themeStyle } from '../../utils/ticketing';
import { usePageTab } from '../../utils/pageTab';

/** Page frame for the premium registration pages: themed background, optional header strip, footer. */
export const TicketingShell = ({ view, back, title, subtitle, children }) => {
  const ticketing = view?.ticketing || {};
  usePageTab({ title: view?.pageTitle || view?.name, icon: view?.faviconUrl });

  return (
    <div className="min-h-screen font-sans text-gray-900" style={themeStyle(ticketing.theme)}>
      {title && (
        <header className="relative overflow-hidden bg-[var(--tk-header)] text-white">
          {ticketing.theme?.headerArtUrl && (
            <img src={ticketing.theme.headerArtUrl} alt="" className="pointer-events-none absolute right-0 top-0 h-full w-auto max-w-[40%] object-contain opacity-90" />
          )}
          <div className="relative mx-auto flex max-w-6xl items-center gap-3 px-4 py-5 sm:px-6 sm:py-6">
            {back && (
              <Link to={back} className="-ml-1 rounded-full p-1.5 hover:bg-white/10" aria-label="Back">
                <ChevronLeft className="h-6 w-6" />
              </Link>
            )}
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold sm:text-2xl">{title}</p>
              {subtitle && <p className="truncate text-sm text-white/85 sm:text-base">{subtitle}</p>}
            </div>
          </div>
        </header>
      )}
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>
      <TicketingFooter
        show={view?.showPoweredBy !== false}
        logos={[...(view?.poweredByLogos || []), ...(ticketing.footerLogos || [])]}
      />
    </div>
  );
};

export const TicketingFooter = ({ logos = [], show = true }) => {
  if (show === false) return null;
  const unique = [...new Set((logos || []).filter(Boolean))];
  return (
  <footer className="mx-auto max-w-6xl px-4 pb-8 sm:px-6">
    <div className="flex flex-col items-center gap-3 border-t border-black/10 pt-6">
      <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Powered by</span>
      <div className="flex flex-wrap items-center justify-center gap-6">
        {unique.map((src) => <img key={src} src={src} alt="" className="h-9 w-auto object-contain opacity-80" />)}
        <a href="/" className="opacity-80 transition hover:opacity-100">
          <img src="/logo-header.png" alt="EventEase" className="h-8 w-auto" />
        </a>
      </div>
    </div>
  </footer>
  );
};

export const Card = ({ className = '', children }) => (
  <section className={`rounded-2xl border border-black/5 bg-white shadow-sm ${className}`}>{children}</section>
);

/** Accessible modal used by the share and instructions dialogs. */
export const Modal = ({ open, onClose, title, children, footer }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {footer && <div className="border-t px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
};

export const PrimaryButton = ({ className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--tk-primary)] px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  />
);
